import React from 'react';

interface CourseItemProps {
  title: string;
  cover: string;
  description: string;
  duration: string;
  onPress: () => void;
  withPadding?: boolean;
  isVideoItem?: boolean;
}

/**
 * CourseItem - Displays a course card with image, title, description and duration
 *
 * @component
 * @example
 * ```tsx
 * <CourseItem
 *   title="React Basics"
 *   cover="/course-image.jpg"
 *   description="Learn React fundamentals"
 *   duration="2h 30m"
 *   onPress={() => handleCourseClick()}
 * />
 * ```
 */
const CourseItem: React.FC<CourseItemProps> = ({ title, cover, description, duration, onPress }) => {
  return (
    <button
      onClick={onPress}
      className="flex flex-col w-full rounded-2xl overflow-hidden bg-white dark:bg-gray-800 border border-solid border-gray-200 dark:border-gray-700 border-width-[0.5px] transition-all duration-300 hover:scale-[1.01] hover:border-primary dark:hover:border-blue-400"
    >
      <div className="relative w-full aspect-[2/1]">
        <img src={cover} alt={title} className="w-full h-full object-cover aspect-[2/1]" />
        <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
          {duration}
        </div>
      </div>

      <div className="p-4 flex flex-col gap-2 justify-start">
        <h3 className="text-left font-semibold text-titleText dark:text-white text-md group-hover:text-primary dark:group-hover:text-blue-400 transition-colors">
          {title}
        </h3>
        <p className="text-left text-descriptionText dark:text-gray-300 text-xs line-clamp-2">{description}</p>
      </div>
    </button>
  );
};

export default CourseItem;
